import { useEffect, useRef, useState } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { useSession } from '@/lib/session';

const ACTIVITY = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'] as const;
const WARNING_SECONDS = 60;

/**
 * Opt-in idle sign-out (§7, `organizations.session_timeout_minutes`). Any activity resets the clock; when it runs
 * out a warning counts down 60 seconds, then the session ends in the "session expired" state with the current
 * address kept for the return trip. While the warning is up only "Stay logged in" keeps the session.
 */
export function IdleGuard({ minutes }: { minutes: number }) {
  const { expireIdle } = useSession();
  const last = useRef(Date.now());
  const [left, setLeft] = useState<number | null>(null);
  const warning = left !== null;

  useEffect(() => {
    if (warning) return;
    const touch = () => { last.current = Date.now(); };
    for (const e of ACTIVITY) window.addEventListener(e, touch, { passive: true });
    return () => { for (const e of ACTIVITY) window.removeEventListener(e, touch); };
  }, [warning]);

  useEffect(() => {
    const t = window.setInterval(() => {
      const idleFor = (Date.now() - last.current) / 1000;
      const limit = minutes * 60;
      if (idleFor < limit) { setLeft(null); return; }
      const remaining = Math.max(0, Math.ceil(limit + WARNING_SECONDS - idleFor));
      if (remaining === 0) { window.clearInterval(t); void expireIdle(); return; }
      setLeft(remaining);
    }, 1000);
    return () => window.clearInterval(t);
  }, [minutes, expireIdle]);

  const stay = () => { last.current = Date.now(); setLeft(null); };
  return (
    <Dialog open={warning} onClose={stay} size="sm" title="Are you still there?"
      description={`For security, you'll be signed out in ${left ?? WARNING_SECONDS} second${left === 1 ? '' : 's'} because nothing has happened for ${minutes} minute${minutes === 1 ? '' : 's'}.`}
      footer={<Button variant="primary" onClick={stay}>Stay logged in</Button>} />
  );
}
